import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardHeader, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Modal } from '../components/ui/Modal'
import { TrainingForm } from '../components/forms/TrainingForm'
import { trainingService } from '../services/trainingService'
import { formatDateTime, getCalendarBadgeData } from '../utils'
import { CalendarDaysIcon, MapPinIcon, UsersIcon, PencilIcon, ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react'
import toast from 'react-hot-toast'

export const TrainingDetailsPage: React.FC = () => {
  const { id, attendance } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const isAttendanceMode = !!attendance

  // Fetch training details
  const { data: training, isLoading: trainingLoading } = useQuery({
    queryKey: ['training', id],
    queryFn: () => trainingService.getTraining(Number(id)),
    enabled: !!id,
  })

  // Fetch attendance data if in attendance mode
  const { data: attendanceData, isLoading: attendanceLoading } = useQuery({
    queryKey: ['training-attendance', id],
    queryFn: () => trainingService.getTrainingAttendance(Number(id)),
    enabled: !!id && isAttendanceMode,
  })

  // Mark attendance mutation
  const markAttendanceMutation = useMutation({
    mutationFn: ({ userId, present }: { userId: number; present: boolean }) =>
      trainingService.markAttendance(Number(id), userId, present),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-attendance', id] })
      queryClient.invalidateQueries({ queryKey: ['training', id] })
      toast.success('Närvaro uppdaterad!')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Kunde inte uppdatera närvaro')
    },
  })

  const handleMarkAttendance = (userId: number, present: boolean) => {
    markAttendanceMutation.mutate({ userId, present })
  }

  if (trainingLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
        <div className="animate-pulse bg-gray-200 rounded-lg h-64"></div>
      </div>
    )
  }

  if (!training?.data) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 mb-4">Träningen hittades inte</div>
        <Button onClick={() => navigate('/')}>
          Tillbaka till startsidan
        </Button>
      </div>
    )
  }

  const trainingData = training.data
  const calendarData = getCalendarBadgeData(trainingData.datum)
  const attendees = attendanceData?.data || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Tillbaka
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isAttendanceMode ? 'Redigera närvaro' : trainingData.titel}
            </h1>
            <p className="text-gray-600">
              {isAttendanceMode ? `Redigera närvaro för ${trainingData.titel}` : 'Träningsdetaljer och närvaro'}
            </p>
          </div>
        </div>
        {!isAttendanceMode && (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(true)}
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Redigera
            </Button>
            <Button onClick={() => navigate(`/training/${id}/attendance`)}>
              <UsersIcon className="h-4 w-4 mr-2" />
              Hantera närvaro
            </Button>
          </div>
        )}
      </div>

      {/* Training Details */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start space-x-6">
            {/* Calendar Badge */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-lg">
                <div className="text-center">
                  <div className="text-lg font-bold text-primary-700">{calendarData.day}</div>
                  <div className="text-xs text-primary-600">{calendarData.month}</div>
                </div>
              </div>
              <div className="text-sm font-medium text-gray-900 mt-1">{calendarData.time}</div>
            </div>

            {/* Training Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{trainingData.titel}</h2>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-4 w-4 mr-1" />
                      {formatDateTime(trainingData.datum)}
                    </div>
                    {trainingData.location && (
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 mr-1" />
                        {trainingData.location}
                      </div>
                    )}
                    <div className="flex items-center">
                      <UsersIcon className="h-4 w-4 mr-1" />
                      {trainingData.anmalda || 0} anmälda
                      {trainingData.max_platser && ` / ${trainingData.max_platser}`}
                    </div>
                  </div>
                </div>

                {/* Badges */}
                <div className="flex space-x-2">
                  {trainingData.bootcamp && (
                    <Badge variant="info">
                      {trainingData.bootcamp_name ? `Bootcamp: ${trainingData.bootcamp_name}` : 'Bootcamp'}
                    </Badge>
                  )}
                  {trainingData.free_training && <Badge variant="success">Gratisträning</Badge>}
                </div>
              </div>

              {trainingData.beskrivning && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Beskrivning</h3>
                  <p className="text-gray-600 whitespace-pre-wrap">{trainingData.beskrivning}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attendance Management */}
      {isAttendanceMode && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">Deltagarlista</h3>
            <p className="text-sm text-gray-600">Markera närvarande deltagare</p>
          </CardHeader>
          <CardContent>
            {attendanceLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-8 bg-gray-200 rounded w-20"></div>
                  </div>
                ))}
              </div>
            ) : attendees.length === 0 ? (
              <div className="text-center py-8">
                <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Inga deltagare anmälda till denna träning</p>
              </div>
            ) : (
              <div className="space-y-3">
                {attendees.map((attendee: any) => (
                  <div
                    key={attendee.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-700">
                          {attendee.namn.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attendee.namn}</p>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          {attendee.completed ? (
                            <span className="flex items-center text-green-600">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Närvarande
                            </span>
                          ) : (
                            <span className="flex items-center text-red-600">
                              <XCircleIcon className="h-3 w-3 mr-1" />
                              Ej närvarande
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant={attendee.completed ? "outline" : "primary"}
                        size="sm"
                        onClick={() => handleMarkAttendance(attendee.id, true)}
                        disabled={markAttendanceMutation.isPending}
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={!attendee.completed ? "outline" : "danger"}
                        size="sm"
                        onClick={() => handleMarkAttendance(attendee.id, false)}
                        disabled={markAttendanceMutation.isPending}
                      >
                        <XCircleIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Training Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Redigera träning"
        size="lg"
      >
        <TrainingForm
          onSuccess={() => setIsEditModalOpen(false)}
          onCancel={() => setIsEditModalOpen(false)}
        />
      </Modal>
    </div>
  )
}
